/**
 * AdminPageView - Pure UI component for admin dashboard
 *
 * This is the View layer that handles only presentation.
 * Contains no logic, hooks, or side effects.
 */
import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminNav from './components/AdminNav';
import type { SystemHealth, AdminMetrics } from './admin.schema';
import type { ActiveProviderInfo, LsPlanCreditsConfig } from './admin.api'; // task-30/31: provider badge + LS credits prop
import { PROVIDER_DISPLAY_NAMES } from './admin.api'; // task-36: shared display names — single source of truth
import './AdminPage.css';

interface AdminPageViewProps {
  systemHealth: SystemHealth | null;
  metrics: AdminMetrics | null;
  loading: boolean;
  error: string | null;
  apiKeyMissing?: boolean;
  activeProvider?: ActiveProviderInfo | null; // task-30: default provider + supported list for badge/selector
  onChangeProvider?: (provider: string) => void; // task-30: handler called when admin selects a different provider
  onResetProvider?: () => void; // task-45: handler to clear persisted DB override and revert to env-var default
  lsPlanCredits?: LsPlanCreditsConfig | null; // task-31: per-plan LS credit config for display and editing
  onUpdateLsPlanCredits?: (updates: Partial<{ default: number; plans: Partial<Record<'basic' | 'premium' | 'enterprise', number>> }>) => Promise<void>; // task-31: save handler
}


function AdminPageView({ systemHealth, metrics, loading, error, apiKeyMissing, activeProvider, onChangeProvider, onResetProvider, lsPlanCredits, onUpdateLsPlanCredits }: AdminPageViewProps) { // task-30/31/45: destructure all props
  const location = useLocation();
  const isDashboard = location.pathname === '/admin';

  // task-53: confirmation state for "Reset to env default" — true while awaiting operator confirmation
  const [resetConfirmPending, setResetConfirmPending] = useState(false);

  // task-53: if the provider source changes externally (e.g. another tab fires a reset, or a
  // live-reload re-fetches data and the source flips to 'env'), dismiss any stale confirmation
  // prompt so the UI does not show "Yes, reset / Cancel" for a button that would be hidden anyway.
  useEffect(() => {
    if (activeProvider?.source !== 'db') setResetConfirmPending(false);
  }, [activeProvider?.source]);

  const handleResetClick = () => setResetConfirmPending(true);
  const handleResetCancel = () => setResetConfirmPending(false);
  const handleResetConfirm = () => {
    setResetConfirmPending(false);
    if (onResetProvider) onResetProvider();
  };

  // task-31: local draft state for LS plan credits form — initialised from prop, kept in sync on prop changes via key
  const [lsDraftDefault, setLsDraftDefault] = useState<string>('');
  const [lsDraftBasic, setLsDraftBasic] = useState<string>('');
  const [lsDraftPremium, setLsDraftPremium] = useState<string>('');
  const [lsDraftEnterprise, setLsDraftEnterprise] = useState<string>('');
  const [lsSaveError, setLsSaveError] = useState<string | null>(null);
  const [lsSaveSuccess, setLsSaveSuccess] = useState(false);

  const handleLsSave = async () => {
    setLsSaveError(null);
    setLsSaveSuccess(false);
    const updates: Partial<{ default: number; plans: Partial<Record<'basic' | 'premium' | 'enterprise', number>> }> = {};
    if (lsDraftDefault.trim() !== '') {
      const v = parseInt(lsDraftDefault, 10);
      if (isNaN(v) || v < 1) { setLsSaveError("Default credits must be a positive integer"); return; }
      updates.default = v;
    }
    const plans: Partial<Record<'basic' | 'premium' | 'enterprise', number>> = {};
    const planEntries: Array<['basic' | 'premium' | 'enterprise', string]> = [
      ['basic', lsDraftBasic],
      ['premium', lsDraftPremium],
      ['enterprise', lsDraftEnterprise],
    ];
    for (const [name, raw] of planEntries) {
      if (raw.trim() !== '') {
        const v = parseInt(raw, 10);
        if (isNaN(v) || v < 1) { setLsSaveError(`${name} credits must be a positive integer`); return; }
        plans[name] = v;
      }
    }
    if (Object.keys(plans).length > 0) updates.plans = plans;
    if (Object.keys(updates).length === 0) { setLsSaveError('No changes to save'); return; }
    try {
      if (onUpdateLsPlanCredits) await onUpdateLsPlanCredits(updates);
      setLsDraftDefault('');
      setLsDraftBasic('');
      setLsDraftPremium('');
      setLsDraftEnterprise('');
      setLsSaveSuccess(true);
      setTimeout(() => setLsSaveSuccess(false), 3000);
    } catch (e) {
      setLsSaveError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  if (loading) {
    return (
      <main className="admin-page-container" role="status" aria-live="polite">
        <div className="admin-page-loading">
          <div className="admin-page-loading-text">
            Loading admin dashboard...
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="admin-page-container" role="alert" aria-live="polite">
        <div className="admin-page-error">
          <div className="admin-page-error-text">
            Error loading admin data
          </div>
          <div>{error}</div>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-page-container">
      <AdminNav />
      <div className="admin-page-content">
        {apiKeyMissing && (
          <div className="admin-page-api-key-missing">
            Analytics routes require an API key. Set VITE_QreditCharge_API_KEY or localStorage.QreditCharge_API_KEY and refresh.
          </div>
        )}
        {isDashboard && (
          <>
            <h1 className="admin-page-title">Admin Dashboard</h1>
            
            {systemHealth && (
              <div className="admin-page-card">
                <h2 className="admin-heading">
                  System Health
                </h2>
                <div className="admin-page-grid">
                  <div className="admin-page-metric">
                    <div id="overall-status-value" className={`admin-metric-value-base ${systemHealth.status === 'healthy' ? 'admin-metric-value-green' : 'admin-metric-value-red'}`} aria-labelledby="overall-status-label">
                      {systemHealth.status.toUpperCase()}
                    </div>
                    <div id="overall-status-label" className="admin-page-metric-label">Overall Status</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="uptime-value" className="admin-metric-value-base admin-metric-value-blue" aria-labelledby="uptime-label">
                      {Math.floor(systemHealth.uptime / 60)}m
                    </div>
                    <div id="uptime-label" className="admin-page-metric-label">Uptime</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="memory-usage-value" className="admin-metric-value-base admin-metric-value-purple" aria-labelledby="memory-usage-label">
                      {Math.round((systemHealth.memory.used / systemHealth.memory.total) * 100)}%
                    </div>
                    <div id="memory-usage-label" className="admin-page-metric-label">Memory Usage</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="database-status-value" className={`admin-metric-value-base ${systemHealth.database.connected ? 'admin-metric-value-green' : 'admin-metric-value-red'}`} aria-labelledby="database-status-label">
                      {systemHealth.database.connected ? 'CONNECTED' : 'OFFLINE'}
                    </div>
                    <div id="database-status-label" className="admin-page-metric-label">Database</div>
                  </div>
                </div>
              </div>
            )}

            {/* task-30: Payment Provider card — shows the active provider with a colour-coded badge and
                provides a selector so operators can switch the global provider without editing config. */}
            <div className="admin-page-card">
              <h2 className="admin-heading">Payment Provider</h2>
              <div className="admin-page-grid">
                <div className="admin-page-metric">
                  {/* Coloured badge reflects the active provider — colour-coding is consistent with the
                      webhook events table (braintree=blue, creem=purple, lemonsqueezy=lemon-green). */}
                  <div
                    id="active-provider-value"
                    className={`admin-provider-badge admin-provider-badge-${activeProvider?.defaultProvider ?? 'unknown'}`}
                    aria-labelledby="active-provider-label"
                  >
                    {/* task-30: show human-readable label; fall back to raw key if not in map */}
                    {activeProvider ? (PROVIDER_DISPLAY_NAMES[activeProvider.defaultProvider] ?? activeProvider.defaultProvider) : '—'}
                  </div>
                  {/* task-45: source chip — tells operator whether the active provider was set by an admin
                      switch (persisted in DB) or is just the env-var default (no override stored). */}
                  {activeProvider && (
                    <span
                      id="active-provider-source"
                      className={`admin-provider-source-chip admin-provider-source-chip-${activeProvider.source}`}
                      aria-label={activeProvider.source === 'db' ? 'Source: database override' : 'Source: environment default'}
                    >
                      {activeProvider.source === 'db' ? 'from database' : 'from environment'}
                    </span>
                  )}
                  <div id="active-provider-label" className="admin-page-metric-label">Active Provider</div>
                  {/* task-45/53: reset button — only shown when an admin override is persisted in the DB.
                      task-53: first click opens an inline confirmation prompt; the DELETE call only fires after
                      the operator clicks "Yes, reset" to prevent accidental fat-finger provider switches. */}
                  {activeProvider?.source === 'db' && onResetProvider && (
                    !resetConfirmPending ? (
                      <button
                        id="reset-provider-btn"
                        className="admin-provider-reset-btn"
                        aria-label="Reset provider to environment default"
                        onClick={handleResetClick}
                      >
                        Reset to env default
                      </button>
                    ) : (
                      <div id="reset-provider-confirm" className="admin-provider-reset-confirm" role="group" aria-label="Confirm provider reset">
                        <span className="admin-provider-reset-confirm-msg">Reset to environment default?</span>
                        <div className="admin-provider-reset-confirm-actions">
                          <button
                            id="reset-provider-confirm-btn"
                            className="admin-provider-reset-btn admin-provider-reset-btn-confirm"
                            aria-label="Yes, reset provider to environment default"
                            onClick={handleResetConfirm}
                          >
                            Yes, reset
                          </button>
                          <button
                            id="reset-provider-cancel-btn"
                            className="admin-provider-reset-btn admin-provider-reset-btn-cancel"
                            aria-label="Cancel provider reset"
                            onClick={handleResetCancel}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
                <div className="admin-page-metric">
                  {/* Provider selector — dynamically populated from the backend-registered list so adding
                      a fourth provider in the future requires no frontend change. */}
                  <label htmlFor="global-provider-select" className="admin-page-metric-label">Switch Provider</label>
                  <select
                    id="global-provider-select"
                    aria-label="Switch global payment provider"
                    value={activeProvider?.defaultProvider ?? ''}
                    disabled={!activeProvider || !onChangeProvider}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v && onChangeProvider) onChangeProvider(v); // only fire on actual selection; guard prevents calls without handler
                    }}
                    className="admin-provider-select"
                  >
                    {!activeProvider && <option value="" disabled>Loading…</option>}
                    {activeProvider?.supportedProviders.map((p) => (
                      <option key={p} value={p}>{PROVIDER_DISPLAY_NAMES[p] ?? p}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* task-31: LS Subscription Credits card — shows current per-plan defaults and lets
                admins override them at runtime without redeploying. Changes apply immediately in-process
                and reset on the next server restart; permanent changes need env-var updates. */}
            <div className="admin-page-card">
              <h2 className="admin-heading">Lemon Squeezy Subscription Credits</h2>
              <p className="admin-page-metric-label" style={{marginBottom:'0.75rem'}}>
                Credits granted per subscription payment. Enter a new value and press Save to override at runtime. Leave blank to keep the current value.
              </p>
              <div className="admin-page-grid">
                <div className="admin-page-metric">
                  <div id="ls-credits-default-value" className="admin-metric-value-base admin-metric-value-blue" aria-labelledby="ls-credits-default-label">
                    {lsPlanCredits ? lsPlanCredits.default : '—'}
                  </div>
                  <div id="ls-credits-default-label" className="admin-page-metric-label">Default (all plans)</div>
                  <input
                    id="ls-credits-default-input"
                    type="number"
                    min="1"
                    aria-label="New default credits"
                    placeholder="New default…"
                    value={lsDraftDefault}
                    onChange={(e) => setLsDraftDefault(e.target.value)}
                    className="admin-provider-select"
                    style={{marginTop:'0.5rem'}}
                  />
                </div>
                <div className="admin-page-metric">
                  <div id="ls-credits-basic-value" className="admin-metric-value-base admin-metric-value-green" aria-labelledby="ls-credits-basic-label">
                    {lsPlanCredits?.plans?.basic ?? '—'}
                  </div>
                  <div id="ls-credits-basic-label" className="admin-page-metric-label">Basic plan</div>
                  <input
                    id="ls-credits-basic-input"
                    type="number"
                    min="1"
                    aria-label="New basic plan credits"
                    placeholder="New basic…"
                    value={lsDraftBasic}
                    onChange={(e) => setLsDraftBasic(e.target.value)}
                    className="admin-provider-select"
                    style={{marginTop:'0.5rem'}}
                  />
                </div>
                <div className="admin-page-metric">
                  <div id="ls-credits-premium-value" className="admin-metric-value-base admin-metric-value-orange" aria-labelledby="ls-credits-premium-label">
                    {lsPlanCredits?.plans?.premium ?? '—'}
                  </div>
                  <div id="ls-credits-premium-label" className="admin-page-metric-label">Premium plan</div>
                  <input
                    id="ls-credits-premium-input"
                    type="number"
                    min="1"
                    aria-label="New premium plan credits"
                    placeholder="New premium…"
                    value={lsDraftPremium}
                    onChange={(e) => setLsDraftPremium(e.target.value)}
                    className="admin-provider-select"
                    style={{marginTop:'0.5rem'}}
                  />
                </div>
                <div className="admin-page-metric">
                  <div id="ls-credits-enterprise-value" className="admin-metric-value-base admin-metric-value-purple" aria-labelledby="ls-credits-enterprise-label">
                    {lsPlanCredits?.plans?.enterprise ?? '—'}
                  </div>
                  <div id="ls-credits-enterprise-label" className="admin-page-metric-label">Enterprise plan</div>
                  <input
                    id="ls-credits-enterprise-input"
                    type="number"
                    min="1"
                    aria-label="New enterprise plan credits"
                    placeholder="New enterprise…"
                    value={lsDraftEnterprise}
                    onChange={(e) => setLsDraftEnterprise(e.target.value)}
                    className="admin-provider-select"
                    style={{marginTop:'0.5rem'}}
                  />
                </div>
              </div>
              <div style={{marginTop:'1rem',display:'flex',alignItems:'center',gap:'1rem',flexWrap:'wrap'}}>
                <button
                  id="ls-credits-save-btn"
                  type="button"
                  className="admin-page-button"
                  onClick={handleLsSave}
                  disabled={!onUpdateLsPlanCredits}
                >
                  Save LS Credits
                </button>
                {lsSaveError && <span style={{color:'var(--color-error,#e53e3e)',fontSize:'0.875rem'}} role="alert">{lsSaveError}</span>}
                {lsSaveSuccess && <span id="ls-credits-save-success" style={{color:'var(--color-success,#38a169)',fontSize:'0.875rem'}} role="status">Saved successfully</span>}
              </div>
            </div>

            {metrics && (
              <div className="admin-page-card">
                <h2 className="admin-heading">
                  Business Metrics
                </h2>
                <div className="admin-page-grid">
                  <div className="admin-page-metric">
                    <div id="total-transactions-value" className="admin-metric-value-base admin-metric-value-green" aria-labelledby="total-transactions-label">
                      {metrics.totalTransactions.toLocaleString()}
                    </div>
                    <div id="total-transactions-label" className="admin-page-metric-label">Total Transactions</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="total-users-value" className="admin-metric-value-base admin-metric-value-blue" aria-labelledby="total-users-label">
                      {metrics.totalUsers.toLocaleString()}
                    </div>
                    <div id="total-users-label" className="admin-page-metric-label">Total Users</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="revenue-value" className="admin-metric-value-base admin-metric-value-orange" aria-labelledby="revenue-label">
                      ${metrics.revenue.toLocaleString()}
                    </div>
                    <div id="revenue-label" className="admin-page-metric-label">Revenue</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="active-subscriptions-value" className="admin-metric-value-base admin-metric-value-purple" aria-labelledby="active-subscriptions-label">
                      {metrics.activeSubscriptions.toLocaleString()}
                    </div>
                    <div id="active-subscriptions-label" className="admin-page-metric-label">Active Subscriptions</div>
                  </div>
                </div>
              </div>
            )}

            <div className="admin-page-card">
              <h2 className="admin-heading">
                Quick Actions
              </h2>
              <div className="admin-page-quick-actions-grid admin-page-grid">
                <button type="button" className="admin-page-button">
                  Refresh Data
                </button>
                <button type="button" className="admin-page-button">
                  Export Logs
                </button>
                <button type="button" className="admin-page-button">
                  System Backup
                </button>
                <button type="button" className="admin-page-button">
                  Clear Cache
                </button>
              </div>
            </div>
          </>
        )}
        <Outlet />
      </div>
    </main>
  );
}

export default AdminPageView;
